/**
 * React Query hooks for User API operations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "@/api/client";
import type { User, CreateUserInput, UpdateUserInput } from "@/api/types";

const USERS_KEY = ["users"];

/**
 * Fetch all users.
 */
export function useUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: () => api.get<User[]>("/users"),
  });
}

/**
 * Fetch a single user by ID.
 */
export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: [...USERS_KEY, id],
    queryFn: () => api.get<User>(`/users/${id as string}`),
    enabled: !!id,
  });
}

/**
 * Create a new user.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateUserInput) => api.post<User>("/users", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

/**
 * Update an existing user.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateUserInput & { id: string }) =>
      api.patch<User>(`/users/${id}`, input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
      queryClient.setQueryData([...USERS_KEY, data.id], data);
    },
  });
}

/**
 * Delete a user.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.del(`/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}
